<template>
    <v-card v-if="drafts.length" class="mb-4">
        <v-data-table
            :hide-default-footer="!toolbarExpanded"
            :headers="toolbarExpanded ? visibleHeaders : []"
            :items="toolbarExpanded ? filteredRows : []"
            v-model:items-per-page="itemsPerPage"
            :items-per-page-options="[
                { value: 10, title: '10' },
                { value: 25, title: '25' },
                { value: 50, title: '50' },
                { value: 100, title: '100' },
            ]"
            density="compact"
            :class="['elevation-0 products-table', { 'products-table-collapsed': !toolbarExpanded }]"
            :fixed-header="draftTableFixedHeader"
            :height="draftTableHeight"
            :row-props="draftRowProps"
        >
            <template v-slot:top>
                <div ref="toolbarRef">
                <v-toolbar class="products-toolbar">
                    <div class="products-toolbar-inner">
                        <v-toolbar-title class="products-toolbar-title subtitle-2">
                            Listă în lucru ({{ displayRows.length }})
                            <span
                                v-if="selectedDraftIds.length && selectedDraftIds.length !== drafts.length"
                                class="text-medium-emphasis"
                            >
                                · {{ selectedDraftIds.length }} selectat{{ selectedDraftIds.length === 1 ? '' : 'e' }}
                            </span>
                        </v-toolbar-title>
                        <div
                            v-if="$vuetify.display.mdAndUp && toolbarExpanded"
                            class="products-toolbar-controls"
                        >
                            <v-text-field
                                class="products-toolbar-field"
                                v-model="search"
                                label="Caută"
                                prepend-inner-icon="mdi-magnify"
                                variant="outlined"
                                density="compact"
                                clearable
                                single-line
                                hide-details="auto"
                            />
                            <div class="products-toolbar-field">
                                <v-menu
                                    v-model="structureFilterMenuOpen"
                                    :close-on-content-click="false"
                                    location="bottom start"
                                >
                                    <template #activator="{ props: menuProps }">
                                        <v-text-field
                                            v-bind="menuProps"
                                            :model-value="structureFilterDisplay"
                                            label="Filtrează"
                                            variant="outlined"
                                            density="compact"
                                            hide-details="auto"
                                            readonly
                                            clearable
                                            prepend-inner-icon="mdi-filter-variant"
                                            append-inner-icon="mdi-chevron-down"
                                            @click:clear.stop="clearStructureFilter"
                                        />
                                    </template>
                                    <v-card min-width="320" elevation="8">
                                        <v-card-text class="d-flex flex-column ga-3 pt-4">
                                            <v-select
                                                v-model="selectedChapterFilter"
                                                :items="chapterFilterOptions"
                                                item-title="title"
                                                item-value="value"
                                                label="Categorie"
                                                variant="outlined"
                                                density="compact"
                                                hide-details
                                                clearable
                                                @update:model-value="onChapterFilterChange"
                                            />
                                            <v-select
                                                v-model="selectedSubchapterFilter"
                                                :items="subchapterFilterOptions"
                                                item-title="title"
                                                item-value="value"
                                                label="Subcategorie"
                                                variant="outlined"
                                                density="compact"
                                                hide-details
                                                clearable
                                                :disabled="!selectedChapterFilter"
                                                @update:model-value="onSubchapterFilterChange"
                                            />
                                        </v-card-text>
                                    </v-card>
                                </v-menu>
                            </div>
                            <div class="products-toolbar-actions">
                                <v-menu :close-on-content-click="false">
                                    <template #activator="{ props: menuProps }">
                                        <v-tooltip location="bottom" text="Coloane vizibile">
                                            <template #activator="{ props: tooltipProps }">
                                                <v-btn
                                                    v-bind="{ ...tooltipProps, ...menuProps }"
                                                    icon
                                                    color="secondary"
                                                >
                                                    <v-icon size="x-small" class="fa fa-gear" />
                                                </v-btn>
                                            </template>
                                        </v-tooltip>
                                    </template>
                                    <v-card class="pa-2" variant="outlined">
                                        <v-list density="compact">
                                            <v-list-item density="compact" hide-details>
                                                <v-checkbox
                                                    v-model="allHeadersVisible"
                                                    :indeterminate="someHeadersVisible && !allHeadersVisible"
                                                    color="info"
                                                    label="Toate"
                                                    hide-details
                                                    density="compact"
                                                />
                                            </v-list-item>
                                            <v-divider class="mb-1" />
                                            <v-list-item
                                                v-for="header in headers"
                                                :key="header.key"
                                                density="compact"
                                                hide-details
                                            >
                                                <v-checkbox
                                                    v-model="header.show"
                                                    color="info"
                                                    :label="header.title"
                                                    hide-details
                                                    density="compact"
                                                />
                                            </v-list-item>
                                        </v-list>
                                    </v-card>
                                </v-menu>
                                <v-btn
                                    icon
                                    color="secondary"
                                    :disabled="publishing || !allDraftIds.length"
                                    @click="toggleSelectAll(!allDraftsSelected)"
                                >
                                    <v-icon>
                                        {{ allDraftsSelected
                                            ? 'mdi-checkbox-multiple-blank-outline'
                                            : 'mdi-checkbox-multiple-marked-outline' }}
                                    </v-icon>
                                    <v-tooltip activator="parent" location="bottom">
                                        {{ allDraftsSelected ? 'Deselectează tot' : 'Selectează tot' }}
                                    </v-tooltip>
                                </v-btn>
                                <v-menu location="bottom end">
                                    <template #activator="{ props: menuProps }">
                                        <v-btn
                                            v-bind="menuProps"
                                            icon
                                            color="danger"
                                            :disabled="publishing"
                                        >
                                            <v-icon>mdi-delete-sweep</v-icon>
                                            <v-tooltip activator="parent" location="bottom">
                                                Șterge
                                            </v-tooltip>
                                        </v-btn>
                                    </template>
                                    <v-list density="compact" min-width="280">
                                        <v-list-item
                                            v-if="!exchangeRateClearOnly"
                                            :disabled="publishing || !selectedDraftIds.length"
                                            @click="emitRemoveSelected"
                                        >
                                            <v-list-item-title>
                                                Șterge selectate{{
                                                    selectedDraftIds.length
                                                        ? ` (${selectedDraftIds.length})`
                                                        : ''
                                                }}
                                            </v-list-item-title>
                                        </v-list-item>
                                        <v-list-item
                                            v-if="canClearAll && exchangeRateClearOnly"
                                            :disabled="publishing"
                                            @click="$emit('clear-exchange-rate')"
                                        >
                                            <v-list-item-title>
                                                Anulează modificările de curs
                                                (revine cursul)
                                            </v-list-item-title>
                                        </v-list-item>
                                        <v-list-item
                                            v-if="canClearAll"
                                            :disabled="publishing"
                                            @click="$emit('clear')"
                                        >
                                            <v-list-item-title>
                                                Golește lista
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-btn
                                    v-if="canPublish"
                                    color="primary"
                                    variant="elevated"
                                    size="small"
                                    style="color: white !important;"
                                    :loading="publishing"
                                    :disabled="publishing || !selectedDraftIds.length"
                                    @click="emitPublish"
                                >
                                    Aplică{{ selectedDraftIds.length ? ` (${selectedDraftIds.length})` : '' }}
                                </v-btn>
                            </div>
                        </div>
                        <div
                            v-if="!toolbarExpanded"
                            class="products-toolbar-spacer"
                        />
                        <v-btn
                            icon
                            variant="text"
                            class="products-toolbar-toggle"
                            :aria-label="toolbarExpanded ? 'Ascunde listă în lucru' : 'Arată listă în lucru'"
                            @click.stop="toggleToolbar"
                        >
                            <v-icon>{{ toolbarExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                    </div>
                </v-toolbar>
                <div
                    v-if="$vuetify.display.smAndDown && toolbarExpanded"
                    class="d-flex flex-column ga-4 px-3 py-3"
                >
                    <v-text-field
                        v-model="search"
                        label="Caută"
                        prepend-inner-icon="mdi-magnify"
                        variant="outlined"
                        density="compact"
                        clearable
                        single-line
                        hide-details="auto"
                    />
                    <v-menu
                        v-model="structureFilterMenuOpen"
                        :close-on-content-click="false"
                        location="bottom start"
                    >
                        <template #activator="{ props: menuProps }">
                            <v-text-field
                                v-bind="menuProps"
                                :model-value="structureFilterDisplay"
                                label="Filtrează"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                readonly
                                clearable
                                prepend-inner-icon="mdi-filter-variant"
                                append-inner-icon="mdi-chevron-down"
                                @click:clear.stop="clearStructureFilter"
                            />
                        </template>
                        <v-card min-width="320" elevation="8">
                            <v-card-text class="d-flex flex-column ga-3 pt-4">
                                <v-select
                                    v-model="selectedChapterFilter"
                                    :items="chapterFilterOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Categorie"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    clearable
                                    @update:model-value="onChapterFilterChange"
                                />
                                <v-select
                                    v-model="selectedSubchapterFilter"
                                    :items="subchapterFilterOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Subcategorie"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    clearable
                                    :disabled="!selectedChapterFilter"
                                    @update:model-value="onSubchapterFilterChange"
                                />
                            </v-card-text>
                        </v-card>
                    </v-menu>
                    <div class="d-flex ga-2 justify-end align-center flex-wrap">
                        <v-menu :close-on-content-click="false">
                            <template #activator="{ props: menuProps }">
                                <v-tooltip location="bottom" text="Coloane vizibile">
                                    <template #activator="{ props: tooltipProps }">
                                        <v-btn
                                            v-bind="{ ...tooltipProps, ...menuProps }"
                                            icon
                                            color="secondary"
                                        >
                                            <v-icon size="x-small" class="fa fa-gear" />
                                        </v-btn>
                                    </template>
                                </v-tooltip>
                            </template>
                            <v-card class="pa-2" variant="outlined">
                                <v-list density="compact">
                                    <v-list-item density="compact" hide-details>
                                        <v-checkbox
                                            v-model="allHeadersVisible"
                                            :indeterminate="someHeadersVisible && !allHeadersVisible"
                                            color="info"
                                            label="Toate"
                                            hide-details
                                            density="compact"
                                        />
                                    </v-list-item>
                                    <v-divider class="mb-1" />
                                    <v-list-item
                                        v-for="header in headers"
                                        :key="header.key"
                                        density="compact"
                                        hide-details
                                    >
                                        <v-checkbox
                                            v-model="header.show"
                                            color="info"
                                            :label="header.title"
                                            hide-details
                                            density="compact"
                                        />
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                        <v-btn
                            icon
                            color="secondary"
                            :disabled="publishing || !allDraftIds.length"
                            @click="toggleSelectAll(!allDraftsSelected)"
                        >
                            <v-icon>
                                {{ allDraftsSelected
                                    ? 'mdi-checkbox-multiple-blank-outline'
                                    : 'mdi-checkbox-multiple-marked-outline' }}
                            </v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                {{ allDraftsSelected ? 'Deselectează tot' : 'Selectează tot' }}
                            </v-tooltip>
                        </v-btn>
                        <v-menu location="bottom end">
                            <template #activator="{ props: menuProps }">
                                <v-btn
                                    v-bind="menuProps"
                                    icon
                                    color="danger"
                                    :disabled="publishing"
                                >
                                    <v-icon>mdi-delete-sweep</v-icon>
                                    <v-tooltip activator="parent" location="bottom">
                                        Șterge
                                    </v-tooltip>
                                </v-btn>
                            </template>
                            <v-list density="compact" min-width="280">
                                <v-list-item
                                    v-if="!exchangeRateClearOnly"
                                    :disabled="publishing || !selectedDraftIds.length"
                                    @click="emitRemoveSelected"
                                >
                                    <v-list-item-title>
                                        Șterge selectate{{
                                            selectedDraftIds.length
                                                ? ` (${selectedDraftIds.length})`
                                                : ''
                                        }}
                                    </v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="canClearAll && exchangeRateClearOnly"
                                    :disabled="publishing"
                                    @click="$emit('clear-exchange-rate')"
                                >
                                    <v-list-item-title>
                                        Anulează modificările de curs
                                        (revine cursul)
                                    </v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    v-if="canClearAll"
                                    :disabled="publishing"
                                    @click="$emit('clear')"
                                >
                                    <v-list-item-title>
                                        Golește lista
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-btn
                            v-if="canPublish"
                            color="primary"
                            variant="elevated"
                            size="small"
                            style="color: white !important;"
                            :loading="publishing"
                            :disabled="publishing || !selectedDraftIds.length"
                            @click="emitPublish"
                        >
                            Aplică{{ selectedDraftIds.length ? ` (${selectedDraftIds.length})` : '' }}
                        </v-btn>
                    </div>
                </div>
                </div>
            </template>
                <template v-slot:[`item.select`]="{ item }">
                    <div class="d-flex justify-center">
                        <v-checkbox
                            :model-value="isDraftSelected(item)"
                            density="compact"
                            hide-details
                            color="secondary"
                            class="ma-0 pa-0 draft-publish-select"
                            :disabled="publishing || !canPublish || !item?.draft?.id"
                            aria-label="Selectează pentru aplicare în listă"
                            @update:model-value="(value) => setDraftSelected(item, value)"
                        />
                        <v-tooltip activator="parent" location="top">
                            Selectează pentru aplicare în listă
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:[`item.serial_number`]="{ item }">
                    <div class="d-flex justify-center">
                        <v-checkbox
                            :model-value="Boolean(item.serial_number)"
                            density="compact"
                            hide-details
                            color="primary"
                            class="ma-0 pa-0"
                            disabled
                        />
                    </div>
                </template>
                <template v-slot:[`item.is_default`]="{ item }">
                    <div class="d-flex justify-center">
                        <v-checkbox
                            :model-value="Boolean(item.is_default)"
                            density="compact"
                            hide-details
                            color="primary"
                            class="ma-0 pa-0"
                            :disabled="publishing || !canChangeDefaultFurnizor(item)"
                            @update:model-value="(value) => onDefaultCheckboxChange(item, value)"
                        />
                    </div>
                </template>
                <template v-slot:[`header.product_code`]="{ column }">
                    <span class="header-title-wrap mr-1">
                        {{ column.title }}
                    </span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="selectedDraftKindFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="true"
                            class="table-dropdown-menu"
                        >
                            <v-card variant="outlined" style="min-width: 220px;">
                                <v-list density="compact">
                                    <v-list-item
                                        :active="!selectedDraftKindFilter"
                                        active-class="active-item"
                                        @click="selectedDraftKindFilter = null"
                                    >
                                        Toate
                                    </v-list-item>
                                    <v-divider />
                                    <v-list-item
                                        :active="selectedDraftKindFilter === 'new'"
                                        active-class="active-item"
                                        @click="selectedDraftKindFilter = 'new'"
                                    >
                                        Nou
                                    </v-list-item>
                                    <v-list-item
                                        :active="selectedDraftKindFilter === 'new_furnizor'"
                                        active-class="active-item"
                                        @click="selectedDraftKindFilter = 'new_furnizor'"
                                    >
                                        Furnizor nou
                                    </v-list-item>
                                    <v-list-item
                                        :active="selectedDraftKindFilter === 'update'"
                                        active-class="active-item"
                                        @click="selectedDraftKindFilter = 'update'"
                                    >
                                        Actualizare
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.product_code`]="{ item }">
                    <div
                        class="d-flex flex-column align-center ga-1 py-1 draft-product-code-cell"
                        :class="{ 'draft-product-code-cell--child': item.is_multi_furnizor_child }"
                    >
                        <div
                            class="d-flex align-center justify-center ga-1"
                            :class="{ 'product-code-cell--child': item.is_multi_furnizor_child }"
                        >
                            <v-btn
                                v-if="item.has_multiple_furnizori && !item.is_multi_furnizor_child"
                                icon
                                variant="text"
                                size="x-small"
                                density="compact"
                                class="multi-furnizor-toggle"
                                :color="isDraftMultiFurnizorExpanded(item) ? 'primary' : undefined"
                                :aria-label="isDraftMultiFurnizorExpanded(item)
                                    ? 'Colapsează furnizorii neschimbați'
                                    : 'Arată și furnizorii neschimbați'"
                                @click.stop="toggleDraftMultiFurnizorExpand(item)"
                            >
                                <v-icon size="small">
                                    {{ isDraftMultiFurnizorExpanded(item)
                                        ? 'mdi-chevron-down'
                                        : 'mdi-chevron-right' }}
                                </v-icon>
                                <v-tooltip activator="parent" location="top">
                                    {{ isDraftMultiFurnizorExpanded(item)
                                        ? 'Colapsează — ascunde furnizorii neschimbați'
                                        : 'Anulează colapsarea — arată toți furnizorii' }}
                                </v-tooltip>
                            </v-btn>
                            <span
                                v-else-if="item.is_multi_furnizor_child"
                                class="multi-furnizor-child-spacer"
                                aria-hidden="true"
                            />
                            <v-chip
                                v-if="showsDraftKindChip(item.draft_kind)"
                                size="x-small"
                                variant="tonal"
                                :color="draftKindChipColor(item.draft_kind)"
                                :prepend-icon="draftKindChipIcon(item.draft_kind)">
                                {{ draftKindLabel(item.draft_kind) }}
                                <v-tooltip
                                    v-if="
                                        item.draft_kind === 'copied'
                                            && item.draft_source_product_code
                                    "
                                    activator="parent"
                                    location="top">
                                    Copiat din {{ item.draft_source_product_code }}
                                </v-tooltip>
                            </v-chip>
                        </div>
                        <span
                            v-if="item.manufacturer_product_code"
                            :class="{ 'text-medium-emphasis': item.is_multi_furnizor_child }"
                        >
                            {{ item.product_code }}
                            <v-tooltip activator="parent" location="top">
                                {{ item.manufacturer_product_code }}
                            </v-tooltip>
                        </span>
                        <span
                            v-else
                            :class="{ 'text-medium-emphasis': item.is_multi_furnizor_child }"
                        >{{ item.product_code }}</span>
                    </div>
                </template>
                <template v-slot:[`item.measurement_unit`]="{ item }">
                    <div class="cell-center">{{ item.measurement_unit || '—' }}</div>
                </template>
                <template v-slot:[`item.product_name`]="{ item }">
                    <span
                        v-if="item.manufacturer_product_name"
                        :style="canMutateDraft(item) ? { cursor: 'pointer' } : undefined"
                        @click="canMutateDraft(item) && $emit('edit', item.draft, item.furnizorTabKey)"
                    >
                        {{ item.product_name }}
                        <v-tooltip activator="parent" location="top">
                            {{ item.manufacturer_product_name }}
                        </v-tooltip>
                    </span>
                    <span
                        v-else
                        :style="canMutateDraft(item) ? { cursor: 'pointer' } : undefined"
                        @click="canMutateDraft(item) && $emit('edit', item.draft, item.furnizorTabKey)"
                    >
                        {{ item.product_name }}
                    </span>
                </template>
                <template v-slot:[`header.status`]="{ column }">
                    <span class="header-title-wrap mr-1">
                        {{ column.title }}
                    </span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="selectedStatusFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="true"
                            class="table-dropdown-menu"
                        >
                            <v-card
                                variant="outlined"
                                style="min-width: 4.5cm; display: flex; flex-direction: column;"
                            >
                                <v-list style="list-style: none;" density="compact">
                                    <v-list-item
                                        :active="!selectedStatusFilter"
                                        active-class="active-item"
                                        @click="selectedStatusFilter = null"
                                    >
                                        Toate
                                    </v-list-item>
                                    <v-divider />
                                    <v-list-item
                                        v-for="option in statusFilterOptions"
                                        :key="option.value"
                                        :active="selectedStatusFilter === option.value"
                                        active-class="active-item"
                                        @click="toggleStatusFilter(option.value)"
                                    >
                                        {{ option.title }}
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <span :style="productStatusTextStyle(item.status)">
                        {{ formatProductStatusLabel(item.status) }}
                    </span>
                </template>
                <template v-slot:[`item.notes`]="{ item }">
                    <v-tooltip
                        v-if="getProductNotes(item)"
                        location="top"
                    >
                        <template #activator="{ props }">
                            <v-icon
                                v-bind="props"
                                size="small"
                                color="primary"
                            >
                                mdi-note-text-outline
                            </v-icon>
                        </template>
                        <div
                            style="max-width: 320px; white-space: pre-wrap; word-break: break-word;"
                        >
                            {{ getProductNotes(item) }}
                        </div>
                    </v-tooltip>
                    <span v-else>—</span>
                </template>
                <template v-slot:[`header.furnizor`]="{ column }">
                    <span class="header-title-wrap mr-1">
                        {{ column.title }}
                    </span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="selectedFurnizorFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                            @update:model-value="(v) => { if (!v) furnizorSearch = ''; }"
                        >
                            <v-card
                                variant="outlined"
                                style="max-height: 11cm; min-width: 4.5cm; display: flex; flex-direction: column;"
                            >
                                <v-card-text class="pa-2 pb-1">
                                    <v-text-field
                                        v-model="furnizorSearch"
                                        label="Caută..."
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        autofocus
                                        @click.stop
                                    />
                                </v-card-text>
                                <v-list style="list-style: none; overflow: auto;" density="compact">
                                    <v-list-item
                                        :active="!selectedFurnizorFilter"
                                        active-class="active-item"
                                        @click="selectedFurnizorFilter = null"
                                    >
                                        Toate
                                    </v-list-item>
                                    <v-divider />
                                    <v-list-item
                                        v-for="option in furnizorFilterOptionsFiltered"
                                        :key="option.value"
                                        :active="selectedFurnizorFilter === option.value"
                                        active-class="active-item"
                                        @click="toggleFurnizorFilter(option.value)"
                                    >
                                        {{ option.title }}
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`header.categories`]="{ column }">
                    <span class="header-title-wrap mr-1">
                        {{ column.title }}
                    </span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="selectedSystemFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                            @update:model-value="(v) => { if (!v) sistemSearch = ''; }"
                        >
                            <v-card
                                variant="outlined"
                                style="max-height: 11cm; min-width: 4.5cm; display: flex; flex-direction: column;"
                            >
                                <v-card-text class="pa-2 pb-1">
                                    <v-text-field
                                        v-model="sistemSearch"
                                        label="Caută..."
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        autofocus
                                        @click.stop
                                    />
                                </v-card-text>
                                <v-list style="list-style: none; overflow: auto;" density="compact">
                                    <v-list-item
                                        :active="!selectedSystemFilter"
                                        active-class="active-item"
                                        @click="selectedSystemFilter = null"
                                    >
                                        Toate
                                    </v-list-item>
                                    <v-divider />
                                    <v-list-item
                                        v-for="option in categoryFilterOptionsFiltered"
                                        :key="option.value"
                                        :active="selectedSystemFilter === option.value"
                                        active-class="active-item"
                                        @click="toggleSystemFilter(option.value)"
                                    >
                                        {{ option.title }}
                                    </v-list-item>
                                </v-list>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.categories`]="{ item }">
                    <div
                        v-if="getProductMainCategories(item).length"
                        class="d-flex flex-wrap ga-1 py-1 justify-center"
                    >
                        <v-chip
                            v-for="categoryName in getProductMainCategories(item)"
                            :key="categoryName"
                            size="x-small"
                            variant="flat"
                            :color="getCategoryColor(categoryName)"
                            style="color: white"
                        >
                            {{ getCategoryShortLabel(categoryName) }}
                        </v-chip>
                    </div>
                    <span v-else class="text-medium-emphasis">—</span>
                </template>
                <template v-slot:[`item.furnizor`]="{ item }">
                    <span v-if="item.furnizor">{{ item.furnizor }}</span>
                    <span v-else class="text-medium-emphasis">—</span>
                </template>
                <template v-slot:[`item.supplier_list_price_old`]="{ item }">
                    {{ formatRonPrice(priorPriceOrNull(item, item.supplier_list_price_old), 2, item.currency) }}
                </template>
                <template v-slot:[`item.supplier_list_price_new`]="{ item }">
                    {{ formatRonPrice(item.supplier_list_price_new, 2, item.currency) }}
                </template>
                <template v-slot:[`item.supplier_list_price_adjustment_ratio`]="{ item }">
                    <span :class="majorareDiffClass(priorPriceOrNull(item, item.supplier_list_price_adjustment_ratio))">
                        {{ formatPercentOrDash(priorPriceOrNull(item, item.supplier_list_price_adjustment_ratio)) }}
                    </span>
                </template>
                <template v-slot:[`item.entry_price_ron_inc_vat_old`]="{ item }">
                    {{ formatRonPrice(priorPriceOrNull(item, item.entry_price_ron_inc_vat_old), 2, true) }}
                </template>
                <template v-slot:[`item.entry_price_ron_inc_vat`]="{ item }">
                    {{ formatRonPrice(item.entry_price_ron_inc_vat, 2, true) }}
                </template>
                <template v-slot:[`header.entry_price_adjustment_ratio`]="{ column }">
                    <span class="header-title-wrap mr-1">{{ column.title }}</span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="hasEntryMajorareFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                        >
                            <v-card variant="outlined" style="min-width: 220px;">
                                <v-card-text class="d-flex flex-column ga-2 pa-3">
                                    <v-select
                                        v-model="entryMajorareFilterOp"
                                        :items="markupFilterOpOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Condiție"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        @update:model-value="onEntryMajorareFilterOpChange"
                                    />
                                    <v-text-field
                                        v-if="entryMajorareFilterOp"
                                        v-model="entryMajorareFilterPercent"
                                        label="Prag (%)"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        type="number"
                                        inputmode="decimal"
                                        suffix="%"
                                    />
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.entry_price_adjustment_ratio`]="{ item }">
                    <span :class="majorareDiffClass(priorPriceOrNull(item, item.entry_price_adjustment_ratio))">
                        {{ formatPercentOrDash(priorPriceOrNull(item, item.entry_price_adjustment_ratio)) }}
                    </span>
                </template>
                <template v-slot:[`header.markup_reference`]="{ column }">
                    <span class="header-title-wrap mr-1">{{ column.title }}</span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="hasMarkupReferenceFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                        >
                            <v-card variant="outlined" style="min-width: 220px;">
                                <v-card-text class="d-flex flex-column ga-2 pa-3">
                                    <v-select
                                        v-model="markupReferenceFilterOp"
                                        :items="markupFilterOpOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Condiție"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        @update:model-value="onMarkupReferenceFilterOpChange"
                                    />
                                    <v-text-field
                                        v-if="markupReferenceFilterOp"
                                        v-model="markupReferenceFilterPercent"
                                        label="Prag (%)"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        type="number"
                                        inputmode="decimal"
                                        suffix="%"
                                    />
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`header.markup_actual`]="{ column }">
                    <span class="header-title-wrap mr-1">{{ column.title }}</span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="hasMarkupActualFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                        >
                            <v-card variant="outlined" style="min-width: 280px;">
                                <v-card-text class="d-flex flex-column ga-2 pa-3">
                                    <v-select
                                        v-model="markupActualFilterOp"
                                        :items="markupActualFilterOpOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Condiție"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        @update:model-value="onMarkupActualFilterOpChange"
                                    />
                                    <v-text-field
                                        v-if="markupActualFilterOp === 'gt' || markupActualFilterOp === 'lt'"
                                        v-model="markupActualFilterPercent"
                                        label="Prag (%)"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        type="number"
                                        inputmode="decimal"
                                        suffix="%"
                                    />
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.markup_reference`]="{ item }">
                    {{ formatPercentOrDash(item.markup_reference) }}
                </template>
                <template v-slot:[`item.markup_actual`]="{ item }">
                    <span :class="markupActualDiffClass(item)">
                        {{ formatPercentOrDash(item.markup_actual) }}
                    </span>
                </template>
                <template v-slot:[`item.previous_price`]="{ item }">
                    {{ formatRonPrice(priorPriceOrNull(item, item.previous_price), 2, true) }}
                </template>
                <template v-slot:[`item.price`]="{ item }">
                    {{ formatRonPrice(item.price, 2, true) }}
                </template>
                <template v-slot:[`header.price_adjustment_ratio`]="{ column }">
                    <span class="header-title-wrap mr-1">{{ column.title }}</span>
                    <v-icon
                        style="cursor: pointer;"
                        size="small"
                        :color="hasListMajorareFilter ? 'primary' : undefined"
                        class="fas fa-chevron-down"
                    >
                        <v-menu
                            activator="parent"
                            :close-on-content-click="false"
                            class="table-dropdown-menu"
                        >
                            <v-card variant="outlined" style="min-width: 220px;">
                                <v-card-text class="d-flex flex-column ga-2 pa-3">
                                    <v-select
                                        v-model="listMajorareFilterOp"
                                        :items="markupFilterOpOptions"
                                        item-title="title"
                                        item-value="value"
                                        label="Condiție"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        @update:model-value="onListMajorareFilterOpChange"
                                    />
                                    <v-text-field
                                        v-if="listMajorareFilterOp"
                                        v-model="listMajorareFilterPercent"
                                        label="Prag (%)"
                                        variant="outlined"
                                        density="compact"
                                        hide-details
                                        clearable
                                        type="number"
                                        inputmode="decimal"
                                        suffix="%"
                                    />
                                </v-card-text>
                            </v-card>
                        </v-menu>
                    </v-icon>
                </template>
                <template v-slot:[`item.price_adjustment_ratio`]="{ item }">
                    <span :class="majorareDiffClass(priorPriceOrNull(item, item.price_adjustment_ratio))">
                        {{ formatPercentOrDash(priorPriceOrNull(item, item.price_adjustment_ratio)) }}
                    </span>
                </template>
                <template v-slot:[`item.draft_modified_by`]="{ item }">
                    {{ formatDraftModifiedBy(item.draft) }}
                </template>
                <template v-slot:[`item.draft_modified_at`]="{ item }">
                    {{ formatDraftModifiedAt(item.draft) }}
                </template>
                <template v-slot:[`item.rowActions`]="{ item }">
                    <div v-if="canMutateDraft(item)" class="draft-row-actions">
                        <v-btn
                            icon
                            variant="text"
                            size="small"
                            @click="$emit('edit', item.draft, item.furnizorTabKey)"
                        >
                            <v-icon color="primary" size="small">mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="!isExchangeRateRecalcDraftRow(item)"
                            icon
                            variant="text"
                            size="small"
                            color="danger"
                            @click="$emit('remove', item.draft)"
                        >
                            <v-icon size="small">mdi-delete</v-icon>
                        </v-btn>
                        <v-btn
                            v-else
                            icon
                            variant="text"
                            size="small"
                            color="danger"
                            @click="emitExchangeRateDraftDeleteBlocked"
                        >
                            <v-icon size="small">mdi-delete</v-icon>
                            <v-tooltip activator="parent" location="top">
                                Draft din schimbarea cursului — folosiți
                                „Anulează modificările de curs”.
                            </v-tooltip>
                        </v-btn>
                    </div>
                    <span v-else class="text-medium-emphasis">—</span>
                </template>
        </v-data-table>
        <v-dialog
            v-model="defaultConfirmOpen"
            width="520"
            :fullscreen="$vuetify.display.smAndDown"
        >
            <v-card>
                <v-card-title class="text-h6">
                    Confirmă produsul implicit
                </v-card-title>
                <v-card-text>
                    <p class="mb-2">
                        Ești sigur că vrei să setezi acest furnizor ca
                        <strong>"implicit"</strong>?
                    </p>
                    <p
                        v-if="defaultConfirmItem"
                        class="mb-0 text-medium-emphasis"
                    >
                        <strong>{{ defaultConfirmItem.product_code }}</strong>
                        —
                        {{ defaultConfirmItem.product_name }}
                        <template v-if="defaultConfirmItem.furnizor">
                            ({{ defaultConfirmItem.furnizor }})
                        </template>
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="text"
                        @click="closeDefaultConfirm"
                    >
                        Anulează
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="text"
                        @click="confirmDefaultFurnizor"
                    >
                        Confirmă
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import {
    MAIN_PRODUCT_CATEGORIES,
    getCategoryColor,
    getCategoryLabel,
    getCategoryShortLabel,
    getProductMainCategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import {
    buildProductStatusFilterOptions,
    formatProductStatus,
    getProductStatusTextStyle,
    normalizeProductStatus,
} from '@/data/productStatus';
import {
    findChapterByCode,
    findSubchapterByCode,
    getChapterDisplayName,
    getChapterName,
    getSubchapterName,
    listAllChapters,
    normalizeChapterCode,
    padSubchapterCode,
} from '@/data/productStructureLookups';
import { ensureProductLookupsLoaded } from '@/utils/productLookupApi';
import { parseProductCodeStructure } from '@/utils/productStructureHelpers';
import {
    formatPercentValue,
    parseNumericInput,
} from '@/utils/priceListProductCalculator';
import {
    buildDraftDisplayRows,
    draftCanChangeDefaultFurnizor,
    resolveProductNotes,
} from '@/utils/priceListTablePricing';
import { formatRonPrice, showAlertModal } from '@/utils/utils';
import {
    filterExchangeRateRecalcDrafts,
    isExchangeRateRecalcDraft,
    resolveExchangeRatePublishSelection,
} from '@/utils/priceListDraftQueue';
import { includesFolded } from '@/utils/textSearch';

export default {
    name: 'PriceListDraftPanel',
    props: {
        drafts: {
            type: Array,
            default: () => [],
        },
        items: {
            type: Array,
            default: () => [],
        },
        publishing: {
            type: Boolean,
            default: false,
        },
        canPublish: {
            type: Boolean,
            default: true,
        },
        canClearAll: {
            type: Boolean,
            default: true,
        },
        /**
         * Lista în lucru contains FX-recalc drafts: those cannot be deleted
         * one-by-one; menu adds „Anulează modificările de curs”.
         */
        exchangeRateClearOnly: {
            type: Boolean,
            default: false,
        },
        /** Verifiers may only edit/delete drafts with status De verificat. */
        restrictActionsToDeVerificat: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'edit',
        'remove',
        'remove-selected',
        'clear',
        'clear-exchange-rate',
        'publish',
        'set-default-furnizor',
    ],
    data() {
        return {
            toolbarExpanded: true,
            itemsPerPage: 25,
            tableMaxHeightPx: Math.max(
                200,
                (typeof window !== 'undefined' ? window.innerHeight : 800) - 198,
            ),
            search: '',
            structureFilterMenuOpen: false,
            productLookupsVersion: 0,
            selectedChapterFilter: null,
            selectedSubchapterFilter: null,
            selectedSystemFilter: null,
            selectedFurnizorFilter: null,
            selectedStatusFilter: null,
            selectedDraftKindFilter: null,
            /** Draft ids with unchanged furnizor siblings expanded (collapsed by default). */
            expandedMultiFurnizorDraftIds: {},
            selectedDraftIds: [],
            sistemSearch: '',
            furnizorSearch: '',
            entryMajorareFilterOp: null,
            entryMajorareFilterPercent: null,
            listMajorareFilterOp: null,
            listMajorareFilterPercent: null,
            markupActualFilterOp: null,
            markupActualFilterPercent: null,
            markupReferenceFilterOp: null,
            markupReferenceFilterPercent: null,
            defaultConfirmOpen: false,
            defaultConfirmItem: null,
            markupFilterOpOptions: [
                { title: 'Toate', value: null },
                { title: 'Mai mare decât', value: 'gt' },
                { title: 'Mai mic decât', value: 'lt' },
            ],
            markupActualFilterOpOptions: [
                { title: 'Toate', value: null },
                { title: 'Mai mare decât', value: 'gt' },
                { title: 'Mai mic decât', value: 'lt' },
                { title: 'Mai mic decât adaos de referință', value: 'lt_ref' },
                { title: 'Mai mare decât adaos de referință', value: 'gt_ref' },
            ],
            headers: [
                {
                    title: 'Aplică',
                    key: 'select',
                    align: 'center',
                    sortable: false,
                    width: '56px',
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                { title: 'Serial nr.', key: 'serial_number', align: 'center', sortable: true, show: true },
                { title: 'Furnizor', key: 'furnizor', align: 'start', sortable: true, show: true },
                { title: 'Cod produs', key: 'product_code', align: 'center', sortable: true, show: true },
                { title: 'Denumire comercială', key: 'product_name', align: 'center', sortable: true, show: true },
                { title: 'Status', key: 'status', align: 'center', sortable: true, show: true },
                {
                    title: 'Produs implicit',
                    key: 'is_default',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                { title: 'Sisteme', key: 'categories', align: 'center', sortable: false, show: true },
                { title: 'U.M.', key: 'measurement_unit', align: 'center', sortable: true, show: true },
                {
                    title: 'Preț listă vechi furnizor',
                    key: 'supplier_list_price_old',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț listă nou furnizor',
                    key: 'supplier_list_price_new',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Diferență',
                    key: 'supplier_list_price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț achiziție vechi fără TVA',
                    key: 'entry_price_ron_inc_vat_old',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț achiziție nou fără TVA',
                    key: 'entry_price_ron_inc_vat',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Majorare achiziție',
                    key: 'entry_price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Adaos referință',
                    key: 'markup_reference',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Adaos actual',
                    key: 'markup_actual',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț vânzare vechi cu TVA',
                    key: 'previous_price',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Preț vânzare nou cu TVA',
                    key: 'price',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Diferență',
                    key: 'price_adjustment_ratio',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                { title: 'Note', key: 'notes', align: 'center', sortable: false, show: true },
                {
                    title: 'Modificat de',
                    key: 'draft_modified_by',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Data modificării',
                    key: 'draft_modified_at',
                    align: 'center',
                    sortable: true,
                    show: true,
                    headerProps: { class: 'header-wrap' },
                },
                {
                    title: 'Acțiuni',
                    key: 'rowActions',
                    sortable: false,
                    align: 'center',
                    show: true,
                    headerProps: { class: 'header-nowrap' },
                    cellProps: { class: 'actions-cell' },
                },
            ],
        };
    },
    async created() {
        try {
            await ensureProductLookupsLoaded();
        } finally {
            this.productLookupsVersion += 1;
        }
    },
    mounted() {
        window.addEventListener('resize', this.calculateHeight);
        this.$nextTick(() => {
            this.calculateHeight();
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.calculateHeight);
    },
    computed: {
        /**
         * Cap table body to viewport when many rows; shrink to content when few
         * (same behaviour as the live products table).
         */
        draftTableHeight() {
            if (!this.toolbarExpanded) {
                return 0;
            }
            const itemsPerPage = Number(this.itemsPerPage) || 25;
            const visibleRows = Math.min(this.filteredRows.length, itemsPerPage);
            if (visibleRows <= 0) {
                return undefined;
            }
            const headerPx = 100;
            const rowPx = 72;
            const estimatedContentPx = headerPx + visibleRows * rowPx;
            if (estimatedContentPx < this.tableMaxHeightPx) {
                return undefined;
            }
            return `${this.tableMaxHeightPx}px`;
        },
        draftTableFixedHeader() {
            return Boolean(this.draftTableHeight);
        },
        statusFilterOptions() {
            const fromItems = this.items || [];
            const fromDrafts = (this.drafts || []).map((draft) => ({
                status: draft?.payload?.status ?? draft?.status,
            }));
            return buildProductStatusFilterOptions([...fromItems, ...fromDrafts]);
        },
        visibleHeaders() {
            return this.headers.filter((header) => header.show);
        },
        allHeadersVisible: {
            get() {
                return this.headers.length > 0 && this.headers.every((header) => header.show);
            },
            set(value) {
                for (const header of this.headers) {
                    header.show = Boolean(value);
                }
            },
        },
        someHeadersVisible() {
            return this.headers.some((header) => header.show);
        },
        liveItemByCode() {
            const map = new Map();
            for (const item of this.items || []) {
                const code = String(item?.product_code || '').trim();
                if (code) {
                    map.set(code, item);
                }
            }
            return map;
        },
        displayRows() {
            const rows = [];
            for (const draft of this.drafts || []) {
                const liveItem = this.resolveLiveItemForDraft(draft);
                const draftId = draft?.id;
                const expanded = draftId != null
                    && Boolean(this.expandedMultiFurnizorDraftIds[String(draftId)]);
                rows.push(...buildDraftDisplayRows(draft, liveItem, { expanded }));
            }
            return rows;
        },
        filteredRows() {
            let result = this.displayRows;

            if (this.selectedSystemFilter) {
                result = result.filter((item) => this.itemMatchesSystemFilter(item));
            }
            if (this.selectedFurnizorFilter) {
                result = result.filter((item) => this.itemMatchesFurnizorFilter(item));
            }
            if (this.selectedStatusFilter) {
                const wanted = String(this.selectedStatusFilter).trim();
                result = result.filter(
                    (item) => String(item.status || '').trim() === wanted,
                );
            }
            if (this.selectedChapterFilter || this.selectedSubchapterFilter) {
                result = result.filter((item) => this.itemMatchesStructureFilter(item));
            }
            if (this.selectedDraftKindFilter) {
                result = result.filter((item) => this.matchesDraftKindFilter(item));
            }
            if (this.hasEntryMajorareFilter) {
                result = result.filter((item) => this.matchesRatioFilter(
                    item.entry_price_adjustment_ratio,
                    this.entryMajorareFilterOp,
                    this.entryMajorareFilterPercent,
                ));
            }
            if (this.hasListMajorareFilter) {
                result = result.filter((item) => this.matchesRatioFilter(
                    item.price_adjustment_ratio,
                    this.listMajorareFilterOp,
                    this.listMajorareFilterPercent,
                ));
            }
            if (this.hasMarkupActualFilter) {
                const op = this.markupActualFilterOp;
                if (op === 'lt_ref' || op === 'gt_ref') {
                    result = result.filter((item) => this.matchesMarkupActualVsReference(item, op));
                } else {
                    result = result.filter((item) => this.matchesRatioFilter(
                        item.markup_actual,
                        op,
                        this.markupActualFilterPercent,
                    ));
                }
            }
            if (this.hasMarkupReferenceFilter) {
                result = result.filter((item) => this.matchesRatioFilter(
                    item.markup_reference,
                    this.markupReferenceFilterOp,
                    this.markupReferenceFilterPercent,
                ));
            }
            if (this.search) {
                result = result.filter((item) => includesFolded(
                    [
                        item.product_code,
                        item.product_name,
                        item.manufacturer_product_name,
                        item.furnizor,
                        item.manufacturer_product_code,
                    ].join(' '),
                    this.search,
                ));
            }
            return result;
        },
        /**
         * Alternating band per product code (same code → same tint, incl. multi-furnizor).
         * First distinct code → stripe 0, next → stripe 1, then 0 again, …
         */
        productCodeStripeByCode() {
            const map = new Map();
            let nextIndex = 0;
            const paletteSize = 2;
            for (const item of this.filteredRows || []) {
                const code = String(item?.product_code || '').trim()
                    || (item?.id != null ? `__row_${item.id}` : '');
                if (!code || map.has(code)) {
                    continue;
                }
                map.set(code, nextIndex % paletteSize);
                nextIndex += 1;
            }
            return map;
        },
        hasEntryMajorareFilter() {
            return Boolean(this.entryMajorareFilterOp)
                && this.hasPercentFilterValue(this.entryMajorareFilterPercent);
        },
        hasListMajorareFilter() {
            return Boolean(this.listMajorareFilterOp)
                && this.hasPercentFilterValue(this.listMajorareFilterPercent);
        },
        isMarkupActualVsRefFilter() {
            return this.markupActualFilterOp === 'lt_ref'
                || this.markupActualFilterOp === 'gt_ref';
        },
        hasMarkupActualFilter() {
            if (!this.markupActualFilterOp) {
                return false;
            }
            if (this.isMarkupActualVsRefFilter) {
                return true;
            }
            return this.hasPercentFilterValue(this.markupActualFilterPercent);
        },
        hasMarkupReferenceFilter() {
            return Boolean(this.markupReferenceFilterOp)
                && this.hasPercentFilterValue(this.markupReferenceFilterPercent);
        },
        categoryFilterOptions() {
            // Always expose the canonical Sistem list (incl. FOTOVOLTAICE),
            // plus any extra categories found on current drafts..
            const names = new Set(
                MAIN_PRODUCT_CATEGORIES.map((category) => category.value),
            );
            for (const item of this.displayRows) {
                for (const categoryName of getProductMainCategories(item)) {
                    names.add(categoryName);
                }
            }
            return Array.from(names)
                .sort((a, b) => getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'ro'))
                .map((name) => ({
                    title: getCategoryLabel(name),
                    value: name,
                }));
        },
        categoryFilterOptionsFiltered() {
            const q = String(this.sistemSearch || '').trim().toLowerCase();
            if (!q) {
                return this.categoryFilterOptions;
            }
            return this.categoryFilterOptions.filter((o) => (
                String(o.title || '').toLowerCase().includes(q)
            ));
        },
        furnizorFilterOptions() {
            const names = new Set();
            for (const item of this.displayRows) {
                const label = String(item.furnizor || '').trim();
                if (label) {
                    names.add(label);
                }
            }
            return Array.from(names)
                .sort((a, b) => a.localeCompare(b, 'ro'))
                .map((name) => ({ title: name, value: name }));
        },
        furnizorFilterOptionsFiltered() {
            const q = String(this.furnizorSearch || '').trim().toLowerCase();
            if (!q) {
                return this.furnizorFilterOptions;
            }
            return this.furnizorFilterOptions.filter((o) => (
                String(o.title || '').toLowerCase().includes(q)
            ));
        },
        chapterFilterOptions() {
            // Full Admin catalog (not only categories present on current drafts).
            void this.productLookupsVersion;
            return listAllChapters()
                .map((chapter) => {
                    const code = normalizeChapterCode(chapter.code);
                    if (!code) {
                        return null;
                    }
                    const name = String(chapter.name || '').trim();
                    return {
                        title: name && name !== code ? `${code} – ${name}` : code,
                        value: code,
                    };
                })
                .filter(Boolean);
        },
        subchapterFilterOptions() {
            // Subcategorii only for the selected Categorie (full catalog for that chapter).
            void this.productLookupsVersion;
            const selectedChapter = normalizeChapterCode(this.selectedChapterFilter);
            if (!selectedChapter) {
                return [];
            }
            const chapter = findChapterByCode(selectedChapter);
            if (!chapter) {
                return [];
            }

            return Object.entries(chapter.subchapters || {})
                .map(([rawCode, rawName]) => {
                    const code = padSubchapterCode(rawCode);
                    if (!code) {
                        return null;
                    }
                    const name = String(rawName || '').trim();
                    return {
                        title: name && name !== code ? `${code} – ${name}` : code,
                        value: `${code}::${name || code}`,
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.title.localeCompare(b.title, 'ro'));
        },
        structureFilterDisplay() {
            const chapter = this.chapterFilterOptions.find(
                (o) => o.value === this.selectedChapterFilter,
            );
            const subchapter = this.subchapterFilterOptions.find(
                (o) => o.value === this.selectedSubchapterFilter,
            );
            if (chapter && subchapter) {
                return `${chapter.title} › ${subchapter.title}`;
            }
            if (chapter) {
                return chapter.title;
            }
            if (subchapter) {
                return subchapter.title;
            }
            return '';
        },
        selectedDraftIdSet() {
            return new Set(
                (this.selectedDraftIds || [])
                    .map((id) => String(id))
                    .filter(Boolean),
            );
        },
        allDraftIds() {
            return (this.drafts || [])
                .map((draft) => draft?.id)
                .filter((id) => id != null && id !== '');
        },
        allDraftsSelected() {
            const ids = this.allDraftIds;
            if (!ids.length) {
                return false;
            }
            return ids.every((id) => this.selectedDraftIdSet.has(String(id)));
        },
    },
    watch: {
        drafts: {
            immediate: true,
            handler(newDrafts, oldDrafts) {
                const newIds = (newDrafts || [])
                    .map((draft) => draft?.id)
                    .filter((id) => id != null && id !== '');
                if (!oldDrafts) {
                    this.selectedDraftIds = [...newIds];
                    return;
                }
                const newIdSet = new Set(newIds.map((id) => String(id)));
                const oldIdSet = new Set(
                    (oldDrafts || [])
                        .map((draft) => draft?.id)
                        .filter((id) => id != null && id !== '')
                        .map((id) => String(id)),
                );
                let kept = (this.selectedDraftIds || []).filter((id) => (
                    newIdSet.has(String(id))
                ));
                const newlyAdded = newIds.filter((id) => !oldIdSet.has(String(id)));
                // Editing a normal product after a curs change must not keep FX
                // drafts selected — otherwise Aplică would also commit the rate.
                const draftById = new Map(
                    (newDrafts || [])
                        .filter((draft) => draft?.id != null && draft.id !== '')
                        .map((draft) => [String(draft.id), draft]),
                );
                const newlyAddedDrafts = newlyAdded
                    .map((id) => draftById.get(String(id)))
                    .filter(Boolean);
                const addedOnlyNonFx = newlyAddedDrafts.length > 0
                    && newlyAddedDrafts.every(
                        (draft) => !isExchangeRateRecalcDraft(draft),
                    );
                if (addedOnlyNonFx) {
                    kept = kept.filter((id) => (
                        !isExchangeRateRecalcDraft(draftById.get(String(id)))
                    ));
                }
                this.selectedDraftIds = [...new Set([...kept, ...newlyAdded])];
            },
        },
    },
    methods: {
        formatRonPrice,
        getCategoryColor,
        getCategoryShortLabel,
        getProductMainCategories,
        formatDraftModifiedBy(draft) {
            if (!draft) {
                return '—';
            }
            return draft.modified_by_name || draft.created_by_name || '—';
        },
        formatDraftModifiedAt(draft) {
            const dateTime = draft?.modifiedAt || draft?.createdAt;
            if (!dateTime) {
                return '—';
            }
            const date = new Date(dateTime);
            if (Number.isNaN(date.getTime())) {
                return String(dateTime);
            }
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
        },
        /**
         * Verifiers may only edit/delete drafts whose displayed status is De verificat.
         * @param {Object} item - Draft display row
         * @returns {boolean}
         */
        canMutateDraft(item) {
            if (!this.restrictActionsToDeVerificat) {
                return true;
            }
            const draft = item?.draft || item;
            const effectiveStatus = normalizeProductStatus(
                item?.status
                ?? draft?.payload?.status
                ?? draft?.builderState?.productBuilderForm?.status,
            );
            return effectiveStatus === 'de_verificat';
        },
        /**
         * @param {Object} draft
         * @returns {Object|null}
         */
        resolveLiveItemForDraft(draft) {
            const candidates = [
                draft?.sourceProductCode,
                draft?.payload?.old_product_code,
                draft?.payload?.product_code,
                draft?.summary?.product_code,
                draft?.builderState?.sessionSourceProductCode,
            ];
            for (const raw of candidates) {
                const code = String(raw || '').trim();
                if (code && this.liveItemByCode.has(code)) {
                    return this.liveItemByCode.get(code);
                }
            }
            return null;
        },
        /**
         * New / copied / new-furnizor rows have no listă veche → force null (UI „—”).
         */
        priorPriceOrNull(item, value) {
            const kind = item?.draft_kind;
            if (kind === 'new' || kind === 'copied' || kind === 'new_furnizor') {
                return null;
            }
            return value;
        },
        draftRowProps({ item }) {
            const classes = [];
            if (item?.is_multi_furnizor_child) {
                classes.push('product-row--furnizor-child');
            }
            const code = String(item?.product_code || '').trim()
                || (item?.id != null ? `__row_${item.id}` : '');
            if (code) {
                const stripe = this.productCodeStripeByCode.get(code);
                if (stripe != null) {
                    classes.push(`draft-row--code-stripe-${stripe}`);
                }
            }
            return classes.length ? { class: classes.join(' ') } : {};
        },
        matchesDraftKindFilter(item) {
            const kind = item?.draft_kind || 'update';
            if (kind === 'unchanged') {
                return false;
            }
            if (this.selectedDraftKindFilter === 'new') {
                return kind === 'new' || kind === 'copied';
            }
            return kind === this.selectedDraftKindFilter;
        },
        showsDraftKindChip(kind) {
            return Boolean(kind) && kind !== 'unchanged';
        },
        /**
         * @param {Object} item
         * @returns {boolean}
         */
        isDraftMultiFurnizorExpanded(item) {
            const draftId = item?.draft?.id;
            if (draftId == null) {
                return false;
            }
            return Boolean(this.expandedMultiFurnizorDraftIds[String(draftId)]);
        },
        /**
         * @param {Object} item
         */
        toggleDraftMultiFurnizorExpand(item) {
            const draftId = item?.draft?.id;
            if (
                draftId == null
                || !item?.has_multiple_furnizori
                || item?.is_multi_furnizor_child
            ) {
                return;
            }
            const key = String(draftId);
            if (this.expandedMultiFurnizorDraftIds[key]) {
                const next = { ...this.expandedMultiFurnizorDraftIds };
                delete next[key];
                this.expandedMultiFurnizorDraftIds = next;
            } else {
                this.expandedMultiFurnizorDraftIds = {
                    ...this.expandedMultiFurnizorDraftIds,
                    [key]: true,
                };
            }
        },
        draftKindLabel(kind) {
            if (kind === 'copied') {
                return 'Nou(copiat)';
            }
            if (kind === 'new') {
                return 'Nou';
            }
            if (kind === 'new_furnizor') {
                return 'Furnizor nou';
            }
            if (kind === 'unchanged') {
                return '';
            }
            return 'Actualizare';
        },
        draftKindChipColor(kind) {
            if (kind === 'copied') {
                return '#00897B';
            }
            if (kind === 'new_furnizor') {
                return 'info';
            }
            if (kind === 'new') {
                return 'success';
            }
            return 'default';
        },
        draftKindChipIcon(kind) {
            if (kind === 'copied') {
                return 'mdi-content-copy';
            }
            if (kind === 'new') {
                return 'mdi-plus-circle-outline';
            }
            if (kind === 'new_furnizor') {
                return 'mdi-truck-plus-outline';
            }
            return 'mdi-pencil-outline';
        },
        isDraftSelected(item) {
            const draftId = item?.draft?.id;
            if (draftId == null || draftId === '') {
                return false;
            }
            return this.selectedDraftIdSet.has(String(draftId));
        },
        setDraftSelected(item, selected) {
            const draftId = item?.draft?.id;
            if (draftId == null || draftId === '' || this.publishing) {
                return;
            }
            const next = new Set(
                (this.selectedDraftIds || []).map((id) => String(id)),
            );
            // FX-recalc drafts are one wave: select/deselect them together.
            const toggleIds = isExchangeRateRecalcDraft(item?.draft)
                ? filterExchangeRateRecalcDrafts(this.drafts)
                    .map((draft) => draft?.id)
                    .filter((id) => id != null && id !== '')
                    .map((id) => String(id))
                : [String(draftId)];
            for (const key of toggleIds) {
                if (selected) {
                    next.add(key);
                } else {
                    next.delete(key);
                }
            }
            const byKey = new Map(
                (this.drafts || [])
                    .filter((draft) => draft?.id != null && draft.id !== '')
                    .map((draft) => [String(draft.id), draft.id]),
            );
            this.selectedDraftIds = [...next].map((id) => byKey.get(id) ?? id);
        },
        toggleSelectAll(selected) {
            if (this.publishing) {
                return;
            }
            // Select/deselect applies to the full working list, not only filtered rows.
            this.selectedDraftIds = selected ? [...this.allDraftIds] : [];
        },
        emitPublish() {
            if (!this.selectedDraftIds.length) {
                showAlertModal(
                    this.$store,
                    'Selectați cel puțin un produs din lista în lucru pentru a-l aplica în listă.',
                    'warning',
                    6000,
                );
                return;
            }
            const fxPublishSelection = resolveExchangeRatePublishSelection({
                drafts: this.drafts,
                selectedIds: this.selectedDraftIds,
            });
            if (!fxPublishSelection.ok) {
                showAlertModal(
                    this.$store,
                    `Modificările de curs afectează ${fxPublishSelection.totalFxCount} produse. `
                    + 'Trebuie să le aplicați pe toate odată (sau pe niciunul) — '
                    + 'nu puteți aplica doar o parte din recalculul de curs.',
                    'warning',
                    10000,
                );
                return;
            }
            this.$emit('publish', [...this.selectedDraftIds]);
        },
        isExchangeRateRecalcDraftRow(item) {
            return isExchangeRateRecalcDraft(item?.draft);
        },
        emitRemoveSelected() {
            if (!this.selectedDraftIds.length) {
                showAlertModal(
                    this.$store,
                    'Selectați cel puțin un produs din lista în lucru pentru a-l șterge.',
                    'warning',
                    6000,
                );
                return;
            }
            this.$emit('remove-selected', [...this.selectedDraftIds]);
        },
        emitExchangeRateDraftDeleteBlocked() {
            showAlertModal(
                this.$store,
                'Acest produs vine din schimbarea cursului valutar și nu poate fi șters individual. Folosiți „Anulează modificările de curs” (cursul revine) sau aplicați modificările în listă. Celelalte draft-uri (fără curs) se pot șterge normal.',
                'warning',
                12000,
            );
        },
        toggleToolbar() {
            this.toolbarExpanded = !this.toolbarExpanded;
            // v-menu content is teleported; explicitly close it when collapsing.
            if (!this.toolbarExpanded) {
                this.structureFilterMenuOpen = false;
            }
            this.$nextTick(() => {
                this.calculateHeight();
            });
        },
        /**
         * Dynamically calculates and updates the max height of the draft table
         * based on the toolbar height and viewport (same as products table).
         */
        calculateHeight() {
            if (this.$refs.toolbarRef) {
                const toolbarHeight = this.$refs.toolbarRef.clientHeight;
                this.tableMaxHeightPx = Math.max(
                    200,
                    window.innerHeight - 134 - toolbarHeight,
                );
            }
        },
        formatPercentOrDash(value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return formatPercentValue(value) || '—';
        },
        canChangeDefaultFurnizor(item) {
            return Boolean(
                this.canMutateDraft(item)
                && item?.furnizorTabKey
                && draftCanChangeDefaultFurnizor(item?.draft),
            );
        },
        onDefaultCheckboxChange(item, nextValue) {
            if (this.publishing || !this.canChangeDefaultFurnizor(item)) {
                return;
            }
            if (item.is_default && !nextValue) {
                showAlertModal(
                    this.$store,
                    'Pentru a schimba produsul implicit, bifați alt furnizor din listă.',
                    'warning',
                    6000,
                );
                return;
            }
            if (!item.is_default && nextValue) {
                this.defaultConfirmItem = item;
                this.defaultConfirmOpen = true;
            }
        },
        closeDefaultConfirm() {
            this.defaultConfirmOpen = false;
            this.defaultConfirmItem = null;
        },
        confirmDefaultFurnizor() {
            const item = this.defaultConfirmItem;
            if (!item?.draft || !item.furnizorTabKey) {
                this.closeDefaultConfirm();
                return;
            }
            const payload = {
                draft: item.draft,
                furnizorTabKey: item.furnizorTabKey,
            };
            this.defaultConfirmOpen = false;
            this.defaultConfirmItem = null;
            this.$emit('set-default-furnizor', payload);
        },
        getProductNotes(item) {
            return resolveProductNotes(item);
        },
        hasPercentFilterValue(value) {
            return value !== null && value !== undefined && String(value).trim() !== '';
        },
        matchesRatioFilter(value, op, percentThreshold) {
            if (value === null || value === undefined || value === '') {
                return false;
            }
            const thresholdRatio = parseNumericInput(percentThreshold) / 100;
            const val = parseNumericInput(value);
            if (op === 'lt') {
                return val < thresholdRatio;
            }
            return val > thresholdRatio;
        },
        /**
         * @param {Object} item
         * @param {'lt_ref'|'gt_ref'} op
         * @returns {boolean}
         */
        matchesMarkupActualVsReference(item, op) {
            if (
                item?.markup_actual === null
                || item?.markup_actual === undefined
                || item?.markup_actual === ''
                || item?.markup_reference === null
                || item?.markup_reference === undefined
                || item?.markup_reference === ''
            ) {
                return false;
            }
            const actual = parseNumericInput(item.markup_actual);
            const reference = parseNumericInput(item.markup_reference);
            const threshold = 0.001;
            if (op === 'lt_ref') {
                return actual < reference - threshold;
            }
            return actual > reference + threshold;
        },
        markupActualDiffClass(item) {
            if (
                item?.markup_actual === null
                || item?.markup_actual === undefined
                || item?.markup_actual === ''
                || item?.markup_reference === null
                || item?.markup_reference === undefined
                || item?.markup_reference === ''
            ) {
                return '';
            }
            const actual = parseNumericInput(item.markup_actual);
            const reference = parseNumericInput(item.markup_reference);
            const threshold = 0.001;
            if (actual < reference - threshold) {
                return 'text-error';
            }
            if (actual > reference + threshold) {
                return 'text-info';
            }
            return '';
        },
        majorareDiffClass(ratio) {
            if (ratio === null || ratio === undefined || ratio === '') {
                return '';
            }
            const numeric = parseNumericInput(ratio);
            if (!Number.isFinite(numeric) || Math.abs(numeric) < 0.0001) {
                return '';
            }
            return numeric > 0 ? 'text-error' : 'text-success';
        },
        toggleSystemFilter(value) {
            this.selectedSystemFilter = this.selectedSystemFilter === value ? null : value;
        },
        toggleFurnizorFilter(value) {
            this.selectedFurnizorFilter = this.selectedFurnizorFilter === value ? null : value;
        },
        toggleStatusFilter(value) {
            this.selectedStatusFilter = this.selectedStatusFilter === value ? null : value;
        },
        formatProductStatusLabel(value) {
            return formatProductStatus(value);
        },
        productStatusTextStyle(value) {
            return getProductStatusTextStyle(value);
        },
        itemMatchesSystemFilter(item) {
            const wanted = normalizeSystemValue(this.selectedSystemFilter);
            if (!wanted) {
                return true;
            }
            return getProductMainCategories(item).some(
                (categoryName) => normalizeSystemValue(categoryName) === wanted,
            );
        },
        itemMatchesFurnizorFilter(item) {
            const wanted = String(this.selectedFurnizorFilter || '').trim().toLowerCase();
            if (!wanted) {
                return true;
            }
            return String(item.furnizor || '').trim().toLowerCase() === wanted;
        },
        itemMatchesStructureFilter(item) {
            const structure = this.getItemStructure(item);
            if (!structure) {
                return false;
            }
            if (
                this.selectedChapterFilter
                && normalizeChapterCode(structure.chapterCode)
                    !== normalizeChapterCode(this.selectedChapterFilter)
            ) {
                return false;
            }
            if (this.selectedSubchapterFilter) {
                const [code, ...nameParts] = String(this.selectedSubchapterFilter).split('::');
                const wantedCode = padSubchapterCode(code);
                const wantedName = nameParts.join('::').trim();
                if (wantedCode && padSubchapterCode(structure.subchapterCode) !== wantedCode) {
                    return false;
                }
                if (
                    wantedName
                    && String(structure.subchapterName || '').trim().toLocaleLowerCase('ro')
                        !== wantedName.toLocaleLowerCase('ro')
                ) {
                    return false;
                }
            }
            return true;
        },
        onChapterFilterChange() {
            const selected = this.selectedSubchapterFilter;
            if (!selected) {
                return;
            }
            const stillValid = this.subchapterFilterOptions.some(
                (option) => option.value === selected,
            );
            if (!stillValid) {
                this.selectedSubchapterFilter = null;
            }
        },
        onSubchapterFilterChange() {
            if (this.selectedSubchapterFilter) {
                this.structureFilterMenuOpen = false;
            }
        },
        clearStructureFilter() {
            this.selectedChapterFilter = null;
            this.selectedSubchapterFilter = null;
            this.structureFilterMenuOpen = false;
        },
        onEntryMajorareFilterOpChange(op) {
            if (!op) {
                this.entryMajorareFilterPercent = null;
            }
        },
        onListMajorareFilterOpChange(op) {
            if (!op) {
                this.listMajorareFilterPercent = null;
            }
        },
        onMarkupActualFilterOpChange(op) {
            if (!op || op === 'lt_ref' || op === 'gt_ref') {
                this.markupActualFilterPercent = null;
            }
        },
        onMarkupReferenceFilterOpChange(op) {
            if (!op) {
                this.markupReferenceFilterPercent = null;
            }
        },
        getItemStructure(item) {
            const detail = item?.pricing_detail;
            const systems = getProductMainCategories(item);
            const preferredSystem = systems[0] || '';

            let chapterCode = normalizeChapterCode(detail?.chapterCode);
            let subchapterCode = padSubchapterCode(detail?.subchapterCode);
            let chapterName = String(detail?.chapterName || '').trim();
            let subchapterName = String(detail?.subchapterName || '').trim();
            const parsed = parseProductCodeStructure(item?.product_code);
            const parsedChapter = normalizeChapterCode(parsed?.chapterCode);
            const parsedSubchapter = padSubchapterCode(parsed?.subchapterCode);

            if (parsedChapter && getChapterDisplayName(parsedChapter) && !getChapterDisplayName(chapterCode)) {
                chapterCode = parsedChapter;
            }
            if (!chapterCode) {
                chapterCode = parsedChapter;
            }
            if (!subchapterCode) {
                subchapterCode = parsedSubchapter;
            }

            chapterCode = normalizeChapterCode(chapterCode);
            if (!chapterCode) {
                return null;
            }

            const catalogChapterName = getChapterDisplayName(chapterCode);
            if (
                catalogChapterName
                && (
                    !chapterName
                    || chapterName === String(detail?.chapterCode || '').trim()
                    || /^\d+$/.test(chapterName)
                )
            ) {
                chapterName = catalogChapterName;
            } else if (!chapterName || chapterName === String(detail?.chapterCode || '').trim()) {
                chapterName = this.resolveChapterName(chapterCode, preferredSystem);
            }
            if (
                subchapterCode
                && (!subchapterName || subchapterName === subchapterCode || /^\d+$/.test(subchapterName))
            ) {
                subchapterName = this.resolveSubchapterName(
                    chapterCode,
                    subchapterCode,
                    preferredSystem,
                );
            }

            return {
                chapterCode,
                chapterName: chapterName || chapterCode,
                subchapterCode,
                subchapterName: subchapterName || subchapterCode,
            };
        },
        resolveChapterName(chapterCode, preferredSystem = '') {
            const catalogName = getChapterDisplayName(chapterCode);
            if (catalogName) {
                return catalogName;
            }
            if (preferredSystem) {
                const name = getChapterName(preferredSystem, chapterCode);
                if (name) {
                    return name;
                }
            }
            for (const category of MAIN_PRODUCT_CATEGORIES) {
                const name = getChapterName(category.value, chapterCode);
                if (name) {
                    return name;
                }
            }
            return '';
        },
        resolveSubchapterName(chapterCode, subchapterCode, preferredSystem = '') {
            const catalogName = findSubchapterByCode(chapterCode, subchapterCode)?.name || '';
            if (catalogName) {
                return catalogName;
            }
            if (preferredSystem) {
                const name = getSubchapterName(preferredSystem, chapterCode, subchapterCode);
                if (name) {
                    return name;
                }
            }
            for (const category of MAIN_PRODUCT_CATEGORIES) {
                const name = getSubchapterName(category.value, chapterCode, subchapterCode);
                if (name) {
                    return name;
                }
            }
            return '';
        },
    },
};
</script>

<style scoped>
.products-toolbar :deep(.v-toolbar__content) {
    height: auto !important;
    min-height: 64px;
    overflow: visible;
    padding-block: 8px;
    padding-inline: 16px;
}

.products-toolbar-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    width: 100%;
    min-width: 0;
}

.products-toolbar-title {
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: none !important;
    margin-inline-end: 0 !important;
    padding-inline-end: 4px;
    white-space: nowrap;
}

.products-toolbar-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 1 1 320px;
    min-width: 0;
}

.products-toolbar-field {
    flex: 1 1 150px;
    min-width: 140px;
    max-width: 240px;
}

.products-toolbar-field :deep(.v-field),
.products-toolbar-field :deep(.v-select__selection-text) {
    min-width: 0;
}

.products-toolbar-field :deep(.v-select__selection-text) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.products-toolbar-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
}

.products-toolbar-toggle {
    flex: 0 0 auto;
}

.products-table-collapsed :deep(.v-table__wrapper) {
    display: none;
}

.products-table-collapsed :deep(.v-data-table-footer) {
    display: none;
}

.products-toolbar-spacer {
    flex: 1 1 auto;
    min-width: 0;
}

.products-table :deep(thead th) {
    font-weight: 700;
    white-space: nowrap;
    vertical-align: middle;
    text-align: center !important;
}

.products-table :deep(.v-data-table-header__content) {
    font-weight: 700;
    white-space: nowrap;
    align-items: center;
    justify-content: center !important;
    text-align: center;
    width: 100%;
}

.products-table :deep(thead th.header-wrap) {
    white-space: normal;
    max-width: 5.5rem;
    vertical-align: middle;
}

.products-table :deep(thead th.header-wrap .v-data-table-header__content) {
    white-space: normal;
    line-height: 1.15;
    text-align: center;
    justify-content: center !important;
}

.products-table :deep(.header-title-wrap) {
    display: inline-block;
    max-width: 4.5rem;
    white-space: normal;
    line-height: 1.15;
    font-weight: 700;
    text-align: center;
    vertical-align: middle;
}

.products-table :deep(tbody td) {
    text-align: center !important;
}

.draft-product-code-cell {
    min-width: 6.5rem;
}

.draft-product-code-cell--child {
    opacity: 0.92;
}

.product-code-cell--child {
    padding-left: 2px;
}

.multi-furnizor-toggle {
    flex: 0 0 auto;
    margin-inline: -4px;
}

.multi-furnizor-child-spacer {
    display: inline-block;
    width: 28px;
    flex: 0 0 28px;
}

.products-table :deep(tr.product-row--furnizor-child td) {
    color: rgba(var(--v-theme-on-surface), 0.82);
}

/*
 * Product-code zebra: same code = same tint (incl. multi-furnizor rows).
 * primary teal vs soft gray-white, with leading edge bar like multi-furnizor groups.
 */
.products-table :deep(tr.draft-row--code-stripe-0 td) {
    background-color: rgba(var(--v-theme-primary), 0.10);
}

.products-table :deep(tr.draft-row--code-stripe-0 td:first-child) {
    box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
    border-left: 1px solid rgba(var(--v-theme-primary), 0.35);
}

.products-table :deep(tr.draft-row--code-stripe-1 td) {
    background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.products-table :deep(tr.draft-row--code-stripe-1 td:first-child) {
    box-shadow: inset 3px 0 0 rgba(var(--v-theme-on-surface), 0.28);
    border-left: 1px solid rgba(var(--v-theme-on-surface), 0.16);
}

.products-table :deep(.cell-center) {
    display: block;
    width: 100%;
    text-align: center;
}

.products-table :deep(td .v-checkbox),
.products-table :deep(td .v-input) {
    display: flex;
    justify-content: center;
}

.products-table :deep(td .v-selection-control) {
    justify-content: center;
}

.products-table :deep(thead th.header-nowrap),
.products-table :deep(td.actions-cell) {
    white-space: nowrap;
    width: 1%;
}

.draft-row-actions {
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}
</style>
